import type { SummaryQuotes } from '@/services/quotes/types';
import { formatDateToBrazil } from '@/shared/utils';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import {
  Avatar,
  Menu,
  Tag,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import { CalendarDays, SquarePen, User } from 'lucide-react';

export type AnalysisAction = 'analysis';

export function createAnalysesColumns(
  // eslint-disable-next-line no-unused-vars
  onAction: (_action: AnalysisAction, _quote: SummaryQuotes) => void
): DataTableColumn<SummaryQuotes>[] {
  return [
    {
      accessorKey: 'insuredName',
      header: 'PROPONENTE',
      size: 220,
      cell: ({ row }) => {
        const quote = row.original;
        return (
          <div className='flex items-center gap-3'>
            <Avatar.Root size='md' shape='circle' className='bg-alba-blue-500'>
              <User className='h-4 w-4 text-white' />
            </Avatar.Root>
            <div className='flex flex-col gap-0.5'>
              <span className='font-montserrat text-sm font-bold text-alba-black-500'>
                {quote.insuredName}
              </span>
              <span className='text-xs text-alba-gray-600'>
                {formatRegisterNumber(quote.registerNumber)}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'brokerName',
      header: 'CORRETOR',
      size: 200,
      cell: ({ row }) => {
        const quote = row.original;
        const brokerName = quote.brokerName
          ? quote.brokerName.split('\n')[0]
          : '-';
        return (
          <span className='font-montserrat text-sm font-normal text-alba-black-500'>
            {brokerName}
          </span>
        );
      },
    },
    {
      accessorKey: 'productName',
      header: 'PRODUTO',
      size: 140,
      cell: ({ row }) => (
        <span className='font-montserrat text-sm font-normal text-alba-black-500'>
          {row.original.productName}
        </span>
      ),
    },
    {
      accessorKey: 'proposalNumber',
      header: 'Nº DA COTAÇÃO',
      size: 200,
      cell: ({ row }) => {
        const quote = row.original;
        return (
          <div className='flex flex-col gap-1'>
            <span className='font-montserrat text-sm font-normal text-alba-black-500'>
              {quote.proposalNumber}
            </span>
            <div className='flex items-center gap-1 text-xs text-alba-gray-600'>
              <CalendarDays className='h-4 w-4 text-alba-gray-600' />
              <span>{formatDateToBrazil(quote.proposalDate)}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'proposalStatus',
      header: 'STATUS DA COTAÇÃO',
      size: 170,
      sortable: false,
      cell: () => (
        <Tag.Root variant='outline' colorScheme='yellow' size='sm'>
          <Tag.Label>Em Análise</Tag.Label>
        </Tag.Root>
      ),
    },
    {
      id: 'actions',
      header: 'AÇÕES',
      size: 60,
      sortable: false,
      cell: ({ row }) => {
        const quote = row.original;
        return (
          <div className='flex justify-end'>
            <Menu.Root>
              <Menu.Trigger />
              <Menu.Content>
                <Menu.Item
                  value='analysis'
                  icon={<SquarePen size={24} strokeWidth={1.6} />}
                  onSelect={() => onAction('analysis', quote)}
                >
                  Análise
                </Menu.Item>
              </Menu.Content>
            </Menu.Root>
          </div>
        );
      },
    },
  ];
}
